import { useState } from 'react';
import {
  RECAPTCHA_CONFIG,
  API_CONFIG,
  CONTACT_FORM_CONFIG,
} from '../config/constants';

const RECAPTCHA_SCRIPT_TIMEOUT_MS = 10000;
const RECAPTCHA_READY_TIMEOUT_MS = 10000;
const RECAPTCHA_SCRIPT_SELECTOR = 'script[data-recaptcha="v3"]';

const createTimeoutError = message => new Error(message);

/**
 * コンタクトフォーム用のカスタムフック
 * @returns {Object} フォームの状態と送信関数
 */
export const useContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | 'bot_detected' | 'disabled' | null

  const waitForReCaptchaScript = script =>
    new Promise((resolve, reject) => {
      let timeoutId;

      function cleanup() {
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
      }

      function handleLoad() {
        cleanup();
        script.dataset.recaptchaStatus = 'loaded';
        resolve();
      }

      function handleError() {
        cleanup();
        script.dataset.recaptchaStatus = 'error';
        reject(createTimeoutError('Failed to load reCAPTCHA'));
      }

      timeoutId = window.setTimeout(() => {
        cleanup();
        script.dataset.recaptchaStatus = 'error';
        reject(createTimeoutError('Timed out loading reCAPTCHA'));
      }, RECAPTCHA_SCRIPT_TIMEOUT_MS);

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
    });

  const waitForReCaptchaReady = () =>
    new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(createTimeoutError('Timed out initializing reCAPTCHA'));
      }, RECAPTCHA_READY_TIMEOUT_MS);

      window.grecaptcha.ready(() => {
        window.clearTimeout(timeoutId);
        resolve();
      });
    });

  const loadReCaptchaScript = async () => {
    if (window.grecaptcha) {
      return;
    }

    const failedScript = document.querySelector(
      `${RECAPTCHA_SCRIPT_SELECTOR}[data-recaptcha-status="error"]`
    );
    if (failedScript) {
      failedScript.remove();
    }

    if (window.grecaptcha) {
      return;
    }

    const existingScript = document.querySelector(RECAPTCHA_SCRIPT_SELECTOR);
    if (existingScript) {
      await waitForReCaptchaScript(existingScript);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_CONFIG.SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = 'v3';
    const scriptLoad = waitForReCaptchaScript(script);
    document.head.appendChild(script);
    await scriptLoad;
  };

  /**
   * reCAPTCHAトークンを取得
   * @returns {Promise<string>} reCAPTCHAトークン
   */
  const getReCaptchaToken = async () => {
    await loadReCaptchaScript();
    await waitForReCaptchaReady();

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(createTimeoutError('Timed out executing reCAPTCHA'));
      }, RECAPTCHA_READY_TIMEOUT_MS);

      window.grecaptcha
        .execute(RECAPTCHA_CONFIG.SITE_KEY, {
          action: RECAPTCHA_CONFIG.ACTION,
        })
        .then(token => {
          window.clearTimeout(timeoutId);
          resolve(token);
        })
        .catch(error => {
          window.clearTimeout(timeoutId);
          reject(error);
        });
    });
  };

  /**
   * フォームデータを送信
   * @param {FormData} formData - フォームデータ
   * @returns {Promise<Object>} 送信結果
   */
  const submitForm = async formData => {
    if (!CONTACT_FORM_CONFIG.IS_ENABLED) {
      setSubmitStatus('disabled');
      return { success: false, error: 'Contact form is disabled' };
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // フォームデータを JSON オブジェクトに変換
      const data = {
        name: formData.get('name'),
        email: formData.get('email'),
        inquiry_type: formData.get('inquiry_type'),
        message: formData.get('message'),
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('送信データ:', data);
      }

      // reCAPTCHA トークンを取得
      const recaptchaToken = await getReCaptchaToken();

      // FormData を使用してリクエストを送信
      const requestFormData = new FormData();
      requestFormData.append('name', data.name);
      requestFormData.append('email', data.email);
      requestFormData.append('inquiry_type', data.inquiry_type);
      requestFormData.append('message', data.message);
      requestFormData.append('recaptcha_token', recaptchaToken);
      requestFormData.append('json_data', JSON.stringify(data));
      requestFormData.append('referer', window.location.origin);

      const response = await fetch(API_CONFIG.GAS_WEB_APP_URL, {
        method: 'POST',
        body: requestFormData,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('レスポンス状態:', response.status);
      }

      // レスポンスの処理
      if (response.ok) {
        const result = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('レスポンス結果:', result);
        }

        if (result.success) {
          setSubmitStatus('success');
          return { success: true, result };
        } else {
          console.error('エラー詳細:', result.error);
          // Bot 検出の場合は専用メッセージを表示
          if (result.error && result.error.includes('Bot detected')) {
            setSubmitStatus('bot_detected');
          } else {
            setSubmitStatus('error');
          }
          return { success: false, error: result.error };
        }
      } else {
        console.error('HTTPエラー:', response.status);
        setSubmitStatus('error');
        return { success: false, error: 'HTTP Error' };
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      return { success: false, error: error.message };
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * フォームをリセット
   */
  const resetForm = () => {
    setSubmitStatus(null);
  };

  return {
    isSubmitting,
    submitStatus,
    submitForm,
    resetForm,
  };
};
